// Tap-to-toggle detail hint with hover on fine-pointer devices; optional single-open scope via PressableHintProvider.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { usePressableHintScope } from './PressableHintContext';

type PressableHintPlacement = 'top' | 'bottom';

type PressableHintProps = {
  /** Stable id within a PressableHintProvider scope; auto-generated when omitted. */
  hintId?: string;
  detail: string;
  ariaLabel: string;
  children: ReactNode;
  rootClassName?: string;
  triggerClassName?: string;
  tooltipClassName?: string;
  placement?: PressableHintPlacement;
};

export const HINT_TOOLTIP_SURFACE_CLASS =
  'rounded-md bg-gray-900 px-2 py-1 text-[10px] font-semibold text-white shadow-md dark:bg-gray-100 dark:text-gray-900';

const TOOLTIP_BASE_CLASS = `pointer-events-none absolute left-1/2 z-[1] -translate-x-1/2 whitespace-nowrap transition-opacity ${HINT_TOOLTIP_SURFACE_CLASS}`;

const placementOffsetClass: Record<PressableHintPlacement, string> = {
  bottom: 'top-full mt-0.5',
  top: 'bottom-full mb-0.5',
};

export const PressableHint = ({
  hintId: hintIdProp,
  detail,
  ariaLabel,
  children,
  rootClassName = '',
  triggerClassName = '',
  tooltipClassName = '',
  placement = 'bottom',
}: PressableHintProps) => {
  const autoId = useId();
  const hintId = hintIdProp ?? autoId;
  const tooltipId = `${hintId}-tooltip`;
  const rootRef = useRef<HTMLDivElement>(null);
  const scope = usePressableHintScope();
  const [localOpen, setLocalOpen] = useState(false);

  const isOpen = scope ? scope.openHintId === hintId : localOpen;

  const handleClose = useCallback((): void => {
    if (scope) {
      scope.closeHints();
      return;
    }
    setLocalOpen(false);
  }, [scope]);

  const handleToggle = useCallback((): void => {
    if (scope) {
      scope.toggleHint(hintId);
      return;
    }
    setLocalOpen((prev) => !prev);
  }, [hintId, scope]);

  const handleTriggerClick = useCallback((): void => {
    handleToggle();
  }, [handleToggle]);

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleClose, handleToggle],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      handleClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [handleClose, isOpen]);

  const tooltipVisibleClass = isOpen
    ? 'opacity-100'
    : 'opacity-0 [@media(hover:hover)]:group-hover/pressable-hint:opacity-100 [@media(hover:hover)]:group-focus-visible/pressable-hint:opacity-100';

  return (
    <div
      ref={rootRef}
      className={`group/pressable-hint relative inline-flex ${rootClassName}`.trim()}
    >
      <button
        type="button"
        className={`inline-flex appearance-none cursor-pointer items-center text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 ${triggerClassName}`.trim()}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        {children}
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className={`${TOOLTIP_BASE_CLASS} ${placementOffsetClass[placement]} ${tooltipVisibleClass} ${tooltipClassName}`.trim()}
      >
        {detail}
      </span>
    </div>
  );
};
